import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Plane, Package, ShoppingCart, Users, Wallet, CreditCard,
  Truck, Receipt, BarChart3, Store, Settings, Menu, LogOut, Bell, X, ClipboardList, KeyRound, Trash2, Search, FileText, LineChart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { ProfileDialog } from "@/components/ProfileDialog";
import { ThemeToggle } from "@/components/ThemeToggle";
import { CommandPalette } from "@/components/CommandPalette";
import { QuickAddFAB } from "@/components/QuickAddFAB";
import { LogoMark } from "@/components/Logo";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/trips", label: "Trip", icon: Plane, testId: "nav-trips" },
  { to: "/products", label: "Produk", icon: Package, testId: "nav-products" },
  { to: "/orders", label: "Order", icon: ShoppingCart, testId: "nav-orders" },
  { to: "/customers", label: "Pelanggan", icon: Users, testId: "nav-customers" },
  { to: "/payments", label: "Pembayaran", icon: Wallet, testId: "nav-payments" },
  { to: "/invoices", label: "Invoice", icon: FileText, testId: "nav-invoices" },
  { to: "/purchases", label: "Pembelian", icon: CreditCard, testId: "nav-purchases" },
  { to: "/shopping-list", label: "Daftar Belanja", icon: ClipboardList, testId: "nav-shopping-list" },
  { to: "/deliveries", label: "Pengiriman", icon: Truck, testId: "nav-deliveries" },
  { to: "/expenses", label: "Pengeluaran", icon: Receipt, testId: "nav-expenses" },
  { to: "/reports", label: "Laporan", icon: BarChart3, testId: "nav-reports" },
  { to: "/catalogue", label: "Katalog", icon: Store, testId: "nav-catalogue" },
  { to: "/analytics", label: "Analitik", icon: LineChart, testId: "nav-analytics" },
  { to: "/recycle-bin", label: "Kotak Sampah", icon: Trash2, testId: "nav-recycle-bin", adminOnly: true },
  { to: "/settings", label: "Pengaturan", icon: Settings, testId: "nav-settings", adminOnly: true },
];

function NavItems({ onClick, isAdmin }) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.filter((n) => !n.adminOnly || isAdmin).map((n) => (
        <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={onClick} data-testid={n.testId}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
              isActive ? "bg-primary/10 text-foreground border border-primary/15 font-medium"
                       : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground border border-transparent"}`}>
          <n.icon className="h-[18px] w-[18px]" />
          {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

function UserFooter({ user, logout, onChangePassword, onProfile }) {
  const shownName = user?.display_name || user?.name;
  return (
    <div className="border-t border-border p-3">
      <div className="flex items-center gap-2 px-2 py-1.5">
        <button onClick={onProfile} data-testid="profile-open-button" title="Pengaturan Profil"
          className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold shrink-0">
          {(shownName || "U")[0]}
        </button>
        <button onClick={onProfile} className="flex-1 min-w-0 text-left" data-testid="profile-menu-button">
          <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{shownName}</p>
          <p className="text-[11px] text-muted-foreground capitalize">{user?.role}</p>
        </button>
        <button onClick={onChangePassword} data-testid="change-password-button" title="Ubah Password"
          className="text-muted-foreground hover:text-primary">
          <KeyRound className="h-4 w-4" />
        </button>
        <button onClick={logout} data-testid="logout-button" title="Keluar" className="text-muted-foreground hover:text-destructive">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-5 py-4">
      <LogoMark className="h-10 w-10" />
      <div className="leading-tight">
        <p className="font-heading font-semibold text-sm text-foreground">Jastip</p>
        <p className="text-[11px] text-muted-foreground">by May Apps</p>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const { trips, selectedTripId, selectTrip } = useApp();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);

  useEffect(() => {
    const match = [...NAV].sort((a, b) => b.to.length - a.to.length)
      .find((n) => location.pathname === n.to || (n.to !== "/" && location.pathname.startsWith(n.to)));
    const label = match ? match.label : "Dashboard";
    document.title = `${label} | Jastip Manager`;
  }, [location.pathname]);

  const loadNotifs = async () => {
    try { const r = await api.get("/notifications"); setNotifs(r.data); }
    catch (e) { console.error("Gagal memuat notifikasi:", e); }
  };
  useEffect(() => { loadNotifs(); }, []);
  const genNotifs = async () => { await api.post("/notifications/generate"); loadNotifs(); };
  const unread = notifs.filter((n) => !n.read).length;

  return (
    <div className="app-shell-bg">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 border-r border-border bg-card z-30">
        <Brand />
        <div className="flex-1 overflow-y-auto pb-4"><NavItems isAdmin={isAdmin} /></div>
        <UserFooter user={user} logout={logout} onChangePassword={() => setPwdOpen(true)} onProfile={() => setProfileOpen(true)} />
      </aside>

      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-card/90 backdrop-blur border-b border-border">
          <div className="flex items-center gap-3 px-4 sm:px-6 h-16">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden text-foreground" data-testid="app-sidebar-toggle"><Menu className="h-6 w-6" /></button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 flex flex-col">
                <Brand />
                <div className="flex-1 overflow-y-auto"><NavItems isAdmin={isAdmin} onClick={() => setMobileOpen(false)} /></div>
                <UserFooter user={user} logout={logout} onChangePassword={() => { setMobileOpen(false); setPwdOpen(true); }} onProfile={() => { setMobileOpen(false); setProfileOpen(true); }} />
              </SheetContent>
            </Sheet>

            <div className="flex-1 flex items-center gap-2 min-w-0">
              <span className="hidden sm:inline text-xs text-muted-foreground">Trip:</span>
              <SearchableSelect
                className="h-9 w-[180px] sm:w-[240px]"
                value={selectedTripId}
                onChange={selectTrip}
                options={[{ value: "all", label: "Semua Trip", keywords: ["semua"] },
                  ...trips.map((t) => ({ value: t.id, label: t.name, keywords: [t.name, t.code] }))]}
                placeholder="Pilih trip"
                searchPlaceholder="Cari trip..."
                emptyText="Trip tidak ditemukan"
                testId="global-trip-filter"
              />
            </div>

            <button onClick={() => setCmdOpen(true)} data-testid="command-palette-open-button"
              className="hidden md:flex items-center gap-2 h-9 pl-3 pr-2 rounded-lg border border-border bg-card text-sm text-muted-foreground hover:bg-secondary/60 transition-colors">
              <Search className="h-4 w-4" /><span>Cari…</span>
              <span className="ml-1 text-[11px] font-mono border border-border rounded px-1.5 py-0.5">⌘K</span>
            </button>
            <button onClick={() => setCmdOpen(true)} data-testid="command-palette-open-button-mobile" aria-label="Cari"
              className="md:hidden h-9 w-9 rounded-lg border border-border bg-card flex items-center justify-center hover:bg-secondary/60">
              <Search className="h-[18px] w-[18px]" />
            </button>

            <ThemeToggle />

            <div className="relative">
              <button onClick={() => { setNotifOpen((v) => !v); }} data-testid="notifications-button"
                className="relative h-9 w-9 rounded-lg border border-border bg-card flex items-center justify-center hover:bg-secondary/60">
                <Bell className="h-[18px] w-[18px]" />
                {unread > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-destructive text-white text-[10px] flex items-center justify-center">{unread}</span>}
              </button>
              {notifOpen && (
                <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-xl border border-border bg-card shadow-lg z-40">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                    <p className="text-sm font-medium">Notifikasi</p>
                    <div className="flex items-center gap-2">
                      <button onClick={genNotifs} className="text-xs text-primary" data-testid="notif-refresh">Perbarui</button>
                      <button onClick={() => setNotifOpen(false)}><X className="h-4 w-4 text-muted-foreground" /></button>
                    </div>
                  </div>
                  {notifs.length === 0 ? <p className="text-sm text-muted-foreground p-4 text-center">Tidak ada notifikasi</p> :
                    notifs.map((n) => (
                      <div key={n.id} className="px-3 py-2.5 border-b border-border/60 last:border-0">
                        <p className="text-sm font-medium">{n.title}</p>
                        <p className="text-xs text-muted-foreground">{n.message}</p>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-8 py-5 sm:py-7 max-w-[1280px] mx-auto">{children}</main>
      </div>

      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
      <QuickAddFAB />
    </div>
  );
}
